import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Globe,
  MagnifyingGlass,
  QrCode,
  UsersThree,
  Truck,
} from "@phosphor-icons/react";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";

export default function PublicLanding() {
  const navigate = useNavigate();
  const [certId, setCertId] = useState("");
  const [error, setError] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const v = certId.trim().toUpperCase();
    if (!v) {
      setError("Enter a certificate number.");
      return;
    }
    setError("");
    navigate(`/verify/${encodeURIComponent(v)}`);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <PublicHeader />

      <section className="relative">
        <div className="absolute inset-0 brand-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 lg:pb-32 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <span className="h-px w-12 bg-slate-400" />
              <span className="label-eyebrow">Official aviation verification registry</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-slate-900 leading-[0.95]">
              Trust, verified.<br />
              <span className="text-[#0B2046]">Globally.</span>
            </h1>
            <p className="mt-8 text-lg text-slate-600 max-w-xl leading-relaxed">
              IAVA is an independent verification platform for aviation credentials —
              cabin operations, safety and ground services. Every certificate
              carries a unique number and QR code that can be authenticated in seconds.
            </p>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white border border-slate-200 shadow-[0_8px_32px_rgba(11,32,70,0.06)] p-8 lg:p-10">
              <div className="flex items-center justify-between">
                <span className="label-eyebrow">Public verification</span>
                <span className="inline-flex items-center gap-1.5 text-xs font-mono-plex text-emerald-700 bg-emerald-50 px-2 py-1">
                  <span className="h-1.5 w-1.5 bg-emerald-500 rounded-full" /> ACTIVE
                </span>
              </div>

              <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-4">
                Verify a credential
              </h2>
              <p className="text-sm text-slate-600 mt-2">
                Enter the certificate number printed on the document or scan its QR code.
              </p>

              <form onSubmit={submit} className="mt-7 space-y-4">
                <label className="label-eyebrow block">Certificate Number</label>
                <div className="flex">
                  <input
                    data-testid="verify-input-id"
                    value={certId}
                    onChange={(e) => setCertId(e.target.value)}
                    placeholder="IAVA-2026-XXXXXX"
                    className="flex-1 px-4 py-3.5 border border-slate-300 font-mono-plex tracking-wider text-[15px] focus:outline-none focus:ring-2 focus:ring-[#0B2046] focus:border-[#0B2046] bg-white"
                  />
                  <button
                    data-testid="verify-submit-button"
                    type="submit"
                    className="px-5 bg-[#0B2046] text-white hover:bg-[#113066] transition-colors inline-flex items-center gap-2 font-medium"
                  >
                    Verify <ArrowRight size={16} weight="bold" />
                  </button>
                </div>
                {error && (
                  <div data-testid="verify-input-error" className="text-sm text-red-600">{error}</div>
                )}
              </form>

              <div className="mt-7 pt-7 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <QrCode size={14} /> QR codes auto-detected
                </span>
                <span className="font-mono-plex">SHA-256 · ICAO Annex 1</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <span className="label-eyebrow">/ 01 — Method</span>
              <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-4">
                Verification in<br />three steps.
              </h2>
              <p className="mt-6 text-slate-600 leading-relaxed">
                Every credential issued through IAVA carries three independent
                verification vectors — we do not trust the document, we trust the
                registry.
              </p>
            </div>
            <div className="lg:col-span-8 grid sm:grid-cols-3 gap-6">
              <Step n="01" title="Lookup" icon={<MagnifyingGlass size={20} />}
                body="Certificate number or QR scan resolves directly against the central registry." />
              <Step n="02" title="Validate" icon={<ShieldCheck size={20} />}
                body="Issuance, expiry, revocation and holder identity are cross-checked in real time." />
              <Step n="03" title="Verdict" icon={<Globe size={20} />}
                body="A signed verification result — accepted by airlines, regulators and ground ops." />
            </div>
          </div>
        </div>
      </section>

      <section id="standards" className="relative">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/27670208/pexels-photo-27670208.jpeg"
            alt="Aviation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0B2046]/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 text-white">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <span className="label-eyebrow text-slate-300">/ 02 — Standards we reference</span>
              <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter mt-4">
                Built around the references<br />the industry already trusts.
              </h2>
            </div>
            <div className="lg:col-span-5 text-slate-200 leading-relaxed">
              <p>
                IAVA references widely recognised aviation industry standards such as
                ICAO Annex 1, EASA Part-CC / Part-66, IATA AHM &amp; DGR, and ISO/IEC 27001.
                We are not affiliated with, endorsed by, or part of any of these organisations —
                we simply align our verification practices with their published guidance.
              </p>
            </div>
          </div>

          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
            {["ICAO", "EASA", "IATA", "ISO"].map((s) => (
              <div key={s} className="bg-[#0B2046] px-6 py-10">
                <div className="font-display text-3xl font-black tracking-tighter">{s}</div>
                <div className="label-eyebrow text-slate-300 mt-2">Reference standard</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CREDENTIAL CLASSES */}
      <section id="credentials" className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-16">
            <div className="lg:col-span-7">
              <span className="label-eyebrow">/ 03 — Coverage</span>
              <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-4">
                Eleven credential classes.<br />Three operational domains.
              </h2>
            </div>
            <div className="lg:col-span-5 text-slate-600 leading-relaxed">
              <p>
                From the cabin to the apron, IAVA verifies the operational
                credentials that keep aviation moving safely.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <CredentialGroup
              icon={<UsersThree size={20} weight="fill" />}
              code="01"
              title="Cabin Operations"
              standard="ICAO Annex 6 · EASA Part-CC · IATA SeMS"
              items={[
                ["CABIN_CREW", "Cabin Crew Attendant"],
                ["CABIN_SENIOR", "Senior Cabin Crew — Purser"],
                ["CABIN_SAFETY", "Cabin Safety Specialist"],
              ]}
            />
            <CredentialGroup
              icon={<ShieldCheck size={20} weight="fill" />}
              code="02"
              title="Safety & Emergency"
              standard="ICAO Doc 9683 · IATA SeMS · NFPA 403"
              items={[
                ["SAFETY_OFFICER", "Aviation Safety Officer"],
                ["EMERGENCY_RESPONSE", "Emergency Response Specialist"],
                ["FIRE_SAFETY", "Fire Safety & Rescue"],
              ]}
            />
            <CredentialGroup
              icon={<Truck size={20} weight="fill" />}
              code="03"
              title="Ground Operations"
              standard="IATA AHM · ICAO Doc 9284 · IATA DGR"
              items={[
                ["GROUND_STAFF", "Ground Handling Agent"],
                ["RAMP_AGENT", "Ramp / Apron Agent"],
                ["LOAD_CONTROL", "Load Control Officer"],
                ["DG_HANDLING", "Dangerous Goods Specialist"],
                ["PASSENGER_SERVICE", "Passenger Service Agent"],
              ]}
            />
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

function Stat({ number, label, mono }) {
  return (
    <div>
      <div className={`font-display text-3xl font-black tracking-tighter text-slate-900 ${mono ? "font-mono-plex" : ""}`}>
        {number}
      </div>
      <div className="label-eyebrow mt-1">{label}</div>
    </div>
  );
}

function Step({ n, title, body, icon }) {
  return (
    <div className="border border-slate-200 p-6 bg-white hover:border-[#0B2046] transition-colors group">
      <div className="flex items-center justify-between">
        <span className="font-mono-plex text-xs text-slate-500">{n}</span>
        <span className="text-slate-400 group-hover:text-[#0B2046] transition-colors">{icon}</span>
      </div>
      <div className="font-display text-xl font-bold tracking-tight text-slate-900 mt-6">{title}</div>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function CredentialGroup({ icon, code, title, standard, items }) {
  return (
    <div className="p-8 lg:p-10 bg-white border border-slate-300">
      <div className="flex items-center justify-between">
        <span className="font-mono-plex text-xs text-slate-500">
          / {code}
        </span>
        <span className="text-[#0B2046]">{icon}</span>
      </div>
      <div className="font-display text-3xl font-black tracking-tighter mt-6 text-slate-900">
        {title}
      </div>
      <div className="label-eyebrow mt-3">
        {standard}
      </div>
      <ul className="mt-8 space-y-3 divide-y divide-slate-100">
        {items.map(([code, label]) => (
          <li key={code} className="flex items-center justify-between pt-3 first:pt-0 first:border-0">
            <span className="text-sm text-slate-800">
              {label}
            </span>
            <span
              data-testid={`landing-cred-${code.toLowerCase()}`}
              className="font-mono-plex text-[11px] tracking-wider px-2 py-0.5 bg-slate-100 text-slate-600"
            >
              {code}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
